/**
 * Тексты вкладок (EN — источник ключей, RU и UK обязаны покрыть каждый). Названия и описания навыков и Tech — ключи из
 * данных баланса (`SkillDef.name/description`, `TechDef.name/description`). Названия героев `hero.<id>.name` задаёт C4 (`ui/windows/messages.ts`).
 */
import type { MessageBundle, TParams } from "../../i18n";

export const tabsEn = {
  "tabs.bar": "Menu",
  "tabs.close": "Close",
  "tabs.heroes": "Heroes",
  "tabs.level": "Level",
  "tabs.stars": "Rank",
  "tabs.tech": "Tech",
  "tabs.coins": "Coins",
  "tabs.lv": "Lv {level}/{max}",
  "tabs.hintLevel": "Upgrade",
  "tabs.hintBuy": "Buy",
  "tabs.power": "Power {n}",
  "tabs.damageNext": "Damage/s {from} → {to}",
  "tabs.damageMax": "Damage/s {n} (max)",
  "tabs.cooldown": "Cooldown {s} s",
  "tabs.unlockWave": "Wave {n}",
  "tabs.lockedAt": "Unlocks at wave {n}",
  "tabs.needLevel": "Needs Lv {n}",
  "tabs.max": "Max",
  "tabs.needCoins": "{n} more",
  "tabs.coinsShort": "Not enough coins",
  "tabs.coinsIncoming": "+{n} on the way",
  "tabs.starNext": "Rank {n}: +{dmg}% damage, skill +15%",
  "tabs.starNextCd": "Rank {n}: +{dmg}% damage, skill +15%, −10% cooldown",
  "tabs.starMilestone": "Rank {star} at Lv {level}",
  "tabs.starsMax": "All 5 ranks",
  "tabs.techLevel": "{level}/{max}",
  "tabs.buy": "Buy",
  "skill.gunner_barrage.name": "Barrage",
  "skill.gunner_barrage.description": "3 s of triple fire rate, 2 bullets per shot",
  "skill.bomber_airstrike.name": "Airstrike",
  "skill.bomber_airstrike.description": "5 bombs on the densest crowd",
  "skill.sparky_chain.name": "Chain lightning",
  "skill.sparky_chain.description": "Lightning jumps between 6 targets",
  "skill.blaze_firewall.name": "Firewall",
  "skill.blaze_firewall.description": "A line of fire burns for 5 s",
  "skill.frosty_freeze.name": "Freeze",
  "skill.frosty_freeze.description": "3 s stop in a circle, then 2 s of −50% speed",
  "tech.hq_armor.name": "Armor",
  "tech.hq_armor.description": "+10% base HP per level",
  "tech.hq_repair.name": "Repair",
  "tech.hq_repair.description": "+30 base HP/s per level",
  "tech.cannon_power.name": "Caliber",
  "tech.cannon_power.description": "+15% cannon damage per level",
  "tech.cannon_reload.name": "Loader",
  "tech.cannon_reload.description": "+8% cannon fire rate per level",
  "tech.cannon_sight.name": "Sight",
  "tech.cannon_sight.description": "+0.5 cells cannon range per level",
  "tech.storage_capacity.name": "Big storage",
  "tech.storage_capacity.description": "+1 h offline income cap per level",
  "tech.storage_press.name": "Coin press",
  "tech.storage_press.description": "+5% all coins per level",
} as const;

export type TabsKey = keyof typeof tabsEn;

export const tabsRu: Record<TabsKey, string> = {
  "tabs.bar": "Меню",
  "tabs.close": "Закрыть",
  "tabs.heroes": "Герои",
  "tabs.level": "Уровень",
  "tabs.stars": "Ранг",
  "tabs.tech": "Штаб",
  "tabs.coins": "Монеты",
  "tabs.lv": "Ур. {level}/{max}",
  "tabs.hintLevel": "Прокачай",
  "tabs.hintBuy": "Купи",
  "tabs.power": "Сила {n}",
  "tabs.damageNext": "Урон/с {from} → {to}",
  "tabs.damageMax": "Урон/с {n} (максимум)",
  "tabs.cooldown": "Перезарядка {s} с",
  "tabs.unlockWave": "Волна {n}",
  "tabs.lockedAt": "Откроется на волне {n}",
  "tabs.needLevel": "Нужен ур. {n}",
  "tabs.max": "Максимум",
  "tabs.needCoins": "Ещё {n}",
  "tabs.coinsShort": "Не хватает монет",
  "tabs.coinsIncoming": "+{n} в пути",
  "tabs.starNext": "Ранг {n}: +{dmg}% урона, навык +15%",
  "tabs.starNextCd": "Ранг {n}: +{dmg}% урона, навык +15%, −10% перезарядки",
  "tabs.starMilestone": "Ранг {star} на ур. {level}",
  "tabs.starsMax": "Все 5 рангов",
  "tabs.techLevel": "{level}/{max}",
  "tabs.buy": "Купить",
  "skill.gunner_barrage.name": "Шквал",
  "skill.gunner_barrage.description": "3 с стреляет втрое чаще, по 2 пули",
  "skill.bomber_airstrike.name": "Авиаудар",
  "skill.bomber_airstrike.description": "5 бомб по самой густой толпе",
  "skill.sparky_chain.name": "Цепная молния",
  "skill.sparky_chain.description": "Молния прыгает по 6 целям",
  "skill.blaze_firewall.name": "Стена огня",
  "skill.blaze_firewall.description": "5 с горит полоса поперёк поля",
  "skill.frosty_freeze.name": "Заморозка",
  "skill.frosty_freeze.description": "3 с стоп в круге, затем 2 с −50% скорости",
  "tech.hq_armor.name": "Броня",
  "tech.hq_armor.description": "+10% прочности штаба за уровень",
  "tech.hq_repair.name": "Ремонт",
  "tech.hq_repair.description": "+30 HP/с штабу за уровень",
  "tech.cannon_power.name": "Калибр",
  "tech.cannon_power.description": "+15% урона пушки за уровень",
  "tech.cannon_reload.name": "Заряжающий",
  "tech.cannon_reload.description": "+8% темпа пушки за уровень",
  "tech.cannon_sight.name": "Прицел",
  "tech.cannon_sight.description": "+0,5 кл. дальности пушки за уровень",
  "tech.storage_capacity.name": "Большой склад",
  "tech.storage_capacity.description": "+1 ч потолка офлайн-дохода за уровень",
  "tech.storage_press.name": "Монетный пресс",
  "tech.storage_press.description": "+5% всех монет за уровень",
};

export const tabsUk: Record<TabsKey, string> = {
  "tabs.bar": "Меню",
  "tabs.close": "Закрити",
  "tabs.heroes": "Герої",
  "tabs.level": "Рівень",
  "tabs.stars": "Ранг",
  "tabs.tech": "Штаб",
  "tabs.coins": "Монети",
  "tabs.lv": "Рів. {level}/{max}",
  "tabs.hintLevel": "Прокачай",
  "tabs.hintBuy": "Купи",
  "tabs.power": "Сила {n}",
  "tabs.damageNext": "Шкода/с {from} → {to}",
  "tabs.damageMax": "Шкода/с {n} (максимум)",
  "tabs.cooldown": "Перезаряджання {s} с",
  "tabs.unlockWave": "Хвиля {n}",
  "tabs.lockedAt": "Відкриється на хвилі {n}",
  "tabs.needLevel": "Потрібен рів. {n}",
  "tabs.max": "Максимум",
  "tabs.needCoins": "Ще {n}",
  "tabs.coinsShort": "Не вистачає монет",
  "tabs.coinsIncoming": "+{n} в дорозі",
  "tabs.starNext": "Ранг {n}: +{dmg}% шкоди, навичка +15%",
  "tabs.starNextCd": "Ранг {n}: +{dmg}% шкоди, навичка +15%, −10% перезаряджання",
  "tabs.starMilestone": "Ранг {star} на рів. {level}",
  "tabs.starsMax": "Усі 5 рангів",
  "tabs.techLevel": "{level}/{max}",
  "tabs.buy": "Купити",
  "skill.gunner_barrage.name": "Шквал",
  "skill.gunner_barrage.description": "3 с стріляє втричі частіше, по 2 кулі",
  "skill.bomber_airstrike.name": "Авіаудар",
  "skill.bomber_airstrike.description": "5 бомб по найгустішому натовпу",
  "skill.sparky_chain.name": "Ланцюгова блискавка",
  "skill.sparky_chain.description": "Блискавка стрибає по 6 цілях",
  "skill.blaze_firewall.name": "Стіна вогню",
  "skill.blaze_firewall.description": "5 с горить смуга впоперек поля",
  "skill.frosty_freeze.name": "Заморозка",
  "skill.frosty_freeze.description": "3 с стоп у колі, потім 2 с −50% швидкості",
  "tech.hq_armor.name": "Броня",
  "tech.hq_armor.description": "+10% міцності штабу за рівень",
  "tech.hq_repair.name": "Ремонт",
  "tech.hq_repair.description": "+30 HP/с штабу за рівень",
  "tech.cannon_power.name": "Калібр",
  "tech.cannon_power.description": "+15% шкоди гармати за рівень",
  "tech.cannon_reload.name": "Заряджальний",
  "tech.cannon_reload.description": "+8% темпу гармати за рівень",
  "tech.cannon_sight.name": "Приціл",
  "tech.cannon_sight.description": "+0,5 кл. дальності гармати за рівень",
  "tech.storage_capacity.name": "Великий склад",
  "tech.storage_capacity.description": "+1 год стелі офлайн-доходу за рівень",
  "tech.storage_press.name": "Монетний прес",
  "tech.storage_press.description": "+5% усіх монет за рівень",
};

export const tabsMessages: MessageBundle<TabsKey> = { en: tabsEn, ru: tabsRu, uk: tabsUk };

/** Функция перевода, которую получает {@link TabsView}: `t` из i18n (или `translate` с языком в тестах). */
export type TabsT = (key: TabsKey | (string & {}), params?: TParams) => string;
